import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

function parseAliases(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/** Stable React list key — entity_id is unique; wikidata_id is not. */
export function entityListKey(e) {
    if (e.entityId != null) return `entity-${e.entityId}`;
    if (e.wikidataId) return `summary-${e.wikidataId}`;
    return `name-${(e.canonicalName || 'unknown').toLowerCase()}`;
}

function entityRankingScore(e) {
    return (Number(e.occurrenceCount) || 0) * 1000 + (Number(e.documentCount) || 0);
}

/**
 * Multiple NER rows can reference the same Wikidata Q-ID. Keep the strongest row
 * for list display and routing; unique list keys still use entityListKey.
 */
export function dedupeEntitiesByWikidataId(entities) {
    const withoutWikidata = [];
    const bestByWikidata = new Map();

    for (const e of entities) {
        if (!e.wikidataId) {
            withoutWikidata.push(e);
            continue;
        }
        const prev = bestByWikidata.get(e.wikidataId);
        if (!prev || entityRankingScore(e) > entityRankingScore(prev)) {
            // Preserve a manual-link signal even if the winning row wasn't the manual one.
            bestByWikidata.set(e.wikidataId, {
                ...e,
                manuallyLinked: !!(e.manuallyLinked || (prev && prev.manuallyLinked)),
            });
        } else if (e.manuallyLinked && !prev.manuallyLinked) {
            prev.manuallyLinked = true;
        }
    }

    return [...bestByWikidata.values(), ...withoutWikidata];
}

/** Align Wikidata/free-form types with NER list chips (ORG, PERSON, …). */
export function normalizeEntityListType(entityType) {
    if (!entityType) return entityType;
    const lower = String(entityType).trim().toLowerCase();
    if (lower === 'business' || lower === 'organization' || lower === 'organisation') {
        return 'ORG';
    }
    if (lower === 'human' || lower === 'person') return 'PERSON';
    return String(entityType).trim().toUpperCase();
}

function normalizeRow(e) {
    return {
        ...e,
        entityType: normalizeEntityListType(e.entityType),
        // Prefer Wikidata Q-id, then chart D-id ref (summary key), then numeric entity_id.
        id: e.wikidataId || e.localEntityRef || String(e.entityId),
        listKey: entityListKey(e),
        image: e.imageUrl || null,
        aliases: parseAliases(e.aliases),
        mentionTexts: Array.isArray(e.mentionTexts) ? e.mentionTexts : [],
        reportCount: Number(e.reportCount) || 0,
        reportTypeCounts: e.reportTypeCounts || {},
        docSourceTypeCounts: e.docSourceTypeCounts || {},
        chartLocal: e.discoveredByModel === 'chart-manual' || Boolean(e.localEntityRef),
    };
}

export const DEFAULT_PAGE_SIZE = 50;

function buildEntityListParams({
    search = '',
    entityType = '',
    tier = '',
    docSourceType = '',
    inDataItems = false,
    inReports = false,
    inCorpus = false,
    noWikidata = false,
    minLinkingScore,
    page,
    pageSize,
} = {}) {
    const params = new URLSearchParams();
    if (page != null) params.set('page', String(page));
    if (pageSize != null) params.set('size', String(pageSize));
    if (search) params.set('search', search);
    if (entityType) params.set('type', entityType);
    if (tier) params.set('tier', tier);
    if (docSourceType) params.set('docSourceType', docSourceType);
    if (inDataItems) params.set('inDataItems', 'true');
    if (inReports) params.set('inReports', 'true');
    if (inCorpus) params.set('inCorpus', 'true');
    if (noWikidata) params.set('noWikidata', 'true');
    if (minLinkingScore != null) params.set('minLinkingScore', String(minLinkingScore));
    return params;
}

/**
 * Server-side-paginated hook for the KB entity list.
 *
 * @param {Object}  options
 * @param {string}  [options.search]       - Search query forwarded to the API
 * @param {string}  [options.entityType]   - Entity type filter forwarded to the API
 * @param {string}  [options.tier]         - 'approved' | 'candidates' | '' (all)
 * @param {string}  [options.docSourceType] - Corpus doc source type filter (e.g. 'PDF', 'OSINT')
 * @param {boolean} [options.inDataItems]  - Restrict to entities appearing in data-library items
 * @param {boolean} [options.inReports]    - Restrict to entities appearing in any report
 * @param {number}  [options.page]         - 0-based page index
 * @param {number}  [options.pageSize]     - Rows per page (default DEFAULT_PAGE_SIZE)
 */
export function useEntities({
    search = '',
    entityType = '',
    tier = '',
    docSourceType = '',
    inDataItems = false,
    inReports = false,
    inCorpus = false,
    noWikidata = false,
    minLinkingScore,
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
} = {}) {
    const location = useLocation();
    const [entities, setEntities] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Entity types from dedicated /entities/types query (full result set, not page 0).
    const [entityTypes, setEntityTypes] = useState([]);

    const kbListPath = location.pathname === '/kb' || location.pathname === '/kb/';
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        const onKbEntitiesChanged = () => setRefreshToken((t) => t + 1);
        window.addEventListener('kb-entities-changed', onKbEntitiesChanged);
        return () => window.removeEventListener('kb-entities-changed', onKbEntitiesChanged);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = buildEntityListParams({
            search, entityType, tier, docSourceType, inDataItems, inReports, inCorpus,
            noWikidata, minLinkingScore, page, pageSize,
        });

        fetch(`${BASE}/api/documents/entities?${params}`, {headers: authHeaders()})
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => {
                if (cancelled) return;
                // Server returns {content, totalElements, totalPages, number, size}
                const raw = Array.isArray(data) ? data : (data.content || []);
                const normalized = dedupeEntitiesByWikidataId(raw.map(normalizeRow));
                setEntities(normalized);
                setTotalElements(data.totalElements ?? normalized.length);
                setTotalPages(data.totalPages ?? 1);
            })
            .catch(err => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [kbListPath, refreshToken, search, entityType, tier, docSourceType, inDataItems, inReports, inCorpus, noWikidata, minLinkingScore, page, pageSize]);

    useEffect(() => {
        let cancelled = false;

        const params = buildEntityListParams({
            search, tier, docSourceType, inDataItems, inReports, inCorpus, noWikidata, minLinkingScore,
        });

        fetch(`${BASE}/api/documents/entities/types?${params}`, {headers: authHeaders()})
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => {
                if (cancelled) return;
                const types = Array.isArray(data?.types) ? data.types : [];
                setEntityTypes(types);
            })
            .catch(() => {
                if (!cancelled) setEntityTypes([]);
            });

        return () => { cancelled = true; };
    }, [kbListPath, refreshToken, search, tier, docSourceType, inDataItems, inReports, inCorpus, noWikidata, minLinkingScore]);

    // Keep entityTypes stable across page changes (set on page 0, cleared when type filter changes).
    const stableEntityTypes = useMemo(() => entityTypes, [entityTypes]);

    return {entities, loading, error, totalElements, totalPages, entityTypes: stableEntityTypes};
}
